import type { FormEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConnectedMailAccount } from '@worki/shared';
import * as platform from '@platform';
import type { MailboxOauthMode } from '../platform/types.js';
import { api, apiUrl, buildQuery, RequestError } from '../lib/api.js';
import { Button, ErrorNotice, Field, Input } from './ui.js';
import { Modal } from './Modal.js';
import { GoogleMark, MicrosoftMark } from './ProviderMarks.js';

/**
 * INV-010 — logging in to a mailbox again, without disconnecting it.
 *
 * A mailbox's login does not last for ever. Google stops honouring a grant after
 * a week while the consent screen is still in testing, and an app password can
 * be revoked from the provider's side at any moment. The scan then fails, and
 * the row used to say "sync error" and nothing more: the only way back was to
 * disconnect the mailbox and connect it again, and disconnecting deletes every
 * invoice the mailbox ever brought in, with its file.
 *
 * So the login is offered where the mailbox already is. The server updates the
 * account it already has and resumes the scan from where it stopped, so the
 * invoices and the folders they were filed into are not touched.
 */

/**
 * Matches the "continue with Google" button on the login screen.
 *
 * Exported for the connect dialog on the invoices page: connecting a mailbox and
 * logging back in to it are the same button, and two copies of its classes are
 * two different buttons the first time one of them is restyled.
 */
export const OAUTH_BUTTON =
  'h-11 w-full rounded-xl border border-border bg-card text-sm font-medium text-foreground ' +
  'flex items-center justify-center gap-2.5 hover:bg-muted transition-colors active:scale-[0.98]';

/**
 * Whether a mailbox is waiting for its owner to log in to it again.
 *
 * The server decides it — a scan that fails with one of
 * `MAILBOX_RECONNECT_ERRORS` leaves the account in `reconnect` — and the row
 * asks, because the answer changes what it offers: a scan started against a
 * mailbox in this state fails exactly the way the last one did, so the sync is
 * swapped for the login.
 */
export function awaitingLogin(account: Pick<ConnectedMailAccount, 'status'>): boolean {
  return account.status === 'reconnect';
}

/**
 * Where the login goes, decided by how the mailbox was connected.
 *
 * A mailbox connected on Google's or Microsoft's consent screen goes back
 * through it, naming the account so the server updates that one rather than
 * adding a second. Everything else — an IMAP server, and the demo driver in
 * local development — was connected with a password, and is asked for it again.
 *
 * Paths rather than URLs, the way `api` takes them. The one that is followed as
 * a plain link is resolved against the API origin by the dialog.
 */
export type ReconnectRoute =
  | {
      kind: 'oauth';
      provider: 'google' | 'microsoft';
      /** Followed as a link, where the page itself may leave for the provider. */
      start: string;
      /** Asked for by a shell that has to open the provider outside itself. */
      url: string;
    }
  | { kind: 'password'; submit: string };

export function reconnectRoute(account: Pick<ConnectedMailAccount, 'id' | 'provider'>): ReconnectRoute {
  const { id, provider } = account;
  if (provider === 'google' || provider === 'microsoft') {
    const query = buildQuery({ reconnect: id });
    return {
      kind: 'oauth',
      provider,
      start: `/invoices/accounts/oauth/${provider}/start${query}`,
      url: `/invoices/accounts/oauth/${provider}/url${query}`,
    };
  }
  return { kind: 'password', submit: `/invoices/accounts/${id}/reconnect` };
}

/**
 * What the dialog shows, apart from the dialog.
 *
 * Everything on it arrives as a prop — the password being typed, the refusal
 * being reported, whether the provider's address is still on its way — so the
 * same body draws for either shell and makes no request of its own. The link
 * arrives already resolved for the same reason: the API origin is a value of
 * the build, and a body that read it could only be drawn inside one.
 */
export function ReconnectMailboxBody({
  account,
  mode,
  href,
  password,
  error,
  opening,
  onPasswordChange,
  onOpenProvider,
  onSubmit,
}: {
  account: Pick<ConnectedMailAccount, 'id' | 'provider'>;
  /** How this shell reaches a provider's screen: `platform.mailboxOauth`. */
  mode: MailboxOauthMode;
  /** The consent route, resolved against the API origin. Followed in `redirect` mode only. */
  href?: string;
  password: string;
  /** An i18n key, as `RequestError` carries it. */
  error: string | null;
  /** The provider's address is being asked for, in `system-browser` mode. */
  opening: boolean;
  onPasswordChange: (password: string) => void;
  onOpenProvider: () => void;
  onSubmit: (event: FormEvent) => void;
}) {
  const { t } = useTranslation();
  const route = reconnectRoute(account);

  return (
    <form onSubmit={onSubmit} className="stack" noValidate>
      {error && <ErrorNotice code={error} />}
      <p className="subtle">{t('invoices.reconnectHint')}</p>

      {/*
        One way in, and only the one the mailbox was connected with. Offering
        the other provider or a password as well would invite logging in to a
        different mailbox than the one whose invoices are on this screen.
      */}
      {route.kind === 'oauth' ? (
        <ProviderLogin
          provider={route.provider}
          mode={mode}
          href={href}
          opening={opening}
          onOpen={onOpenProvider}
        />
      ) : (
        <Field label={t('invoices.appPassword')} htmlFor="reconnect-password" hint={t('invoices.reconnectPasswordHint')}>
          <Input
            id="reconnect-password"
            type="password"
            dir="ltr"
            autoComplete="off"
            autoFocus
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
          />
        </Field>
      )}
    </form>
  );
}

/**
 * The provider's button, reaching its consent screen by whichever route this
 * shell has.
 *
 * On the web it is a link: the page leaves for the provider and comes back to a
 * callback on this origin, which only a real navigation does. A packaged shell
 * cannot follow it — its session is a Bearer token a navigation never carries,
 * and Google refuses consent inside an embedded WebView in any case — so there
 * it is a button that asks for the address and opens it in the system browser.
 */
function ProviderLogin({
  provider,
  mode,
  href,
  opening,
  onOpen,
}: {
  provider: 'google' | 'microsoft';
  mode: MailboxOauthMode;
  href: string | undefined;
  opening: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const mark = provider === 'google' ? <GoogleMark /> : <MicrosoftMark />;
  const label = t(provider === 'google' ? 'invoices.reconnectWithGoogle' : 'invoices.reconnectWithMicrosoft');

  if (mode === 'redirect') {
    return (
      <a href={href} className={OAUTH_BUTTON}>
        {mark}
        {label}
      </a>
    );
  }
  return (
    <button type="button" className={OAUTH_BUTTON} disabled={opening} onClick={onOpen}>
      {mark}
      {opening ? t('common.loading') : label}
    </button>
  );
}

/**
 * The dialog: the request behind each way in, and what a failed one leaves on
 * the screen.
 *
 * Mounted for one mailbox and dropped when it closes, so a password typed for
 * one mailbox is never waiting in the field when the dialog opens for the other.
 */
export function ReconnectMailboxDialog({
  account,
  onClose,
  onReconnected,
}: {
  account: ConnectedMailAccount;
  onClose: () => void;
  /** Handed the account as the server now has it, with its scan already started. */
  onReconnected: (account: ConnectedMailAccount) => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [opening, setOpening] = useState(false);
  const route = reconnectRoute(account);

  /*
   * The shell's half of the trip. Nothing comes back from the system browser,
   * so the dialog closes as it leaves; returning to the app refetches the
   * accounts, and that is where the mailbox shows up logged in again.
   */
  const openProvider = async () => {
    if (route.kind !== 'oauth') return;
    setOpening(true);
    setError(null);
    try {
      const { url } = await api.get<{ url: string }>(route.url);
      await platform.openExternal(url);
      onClose();
    } catch (failure) {
      setError(failure instanceof RequestError ? failure.code : 'invoices.oauthFailed');
    } finally {
      setOpening(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    // Enter in the field submits the form whatever state the button is in, so
    // what its `disabled` stands for is checked here as well.
    if (route.kind !== 'password' || password === '' || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const reconnected = await api.post<ConnectedMailAccount>(route.submit, { password });
      onReconnected(reconnected);
      onClose();
    } catch (failure) {
      setError(failure instanceof RequestError ? failure.code : 'errors.unknown');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      title={t('invoices.reconnectTitle', { email: account.email })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/* A mailbox that logs in on its provider's screen has its whole
              action in the body; only a password has anything to submit. */}
          {route.kind === 'password' && (
            <Button variant="primary" loading={submitting} disabled={password === ''} onClick={submit}>
              {t('invoices.reconnect')}
            </Button>
          )}
        </>
      }
    >
      <ReconnectMailboxBody
        account={account}
        mode={platform.mailboxOauth}
        href={route.kind === 'oauth' ? apiUrl(route.start) : undefined}
        password={password}
        error={error}
        opening={opening}
        onPasswordChange={setPassword}
        onOpenProvider={openProvider}
        onSubmit={submit}
      />
    </Modal>
  );
}
